/*
 * Lista de filtros para la lista de pagos.
 *
 * Contiene los componentes de buscador general, un selector de ubicación, selector
 * de estatus de orden, buscador de creador, selector de estatus de pago, fecha de comprobante,
 * fecha de registro, selector de entrega de efectivo y fecha de entrega.
 * Tambien un componente para indicar las columnas visibles y un botón para
 * descargar un archivo PDF.
 *
 * Created on Mon Sep 25 2023 by José Luis Chávez Ayala.
 *
 * Copyright (c) 2023 Turn My App.
 */

<template>
  <div>

    <b-form-row>

      <!-- #region::Columns for main filters -->
      <b-col md="10">
        <b-form-row>

          <!-- #region::Searcher -->
          <b-col
            cols="2"
            class="mb-1"
          >
            <BasicSearcher
              ref="basic-searcher"
              placeholder="Buscar"
              :callback="handleSearch"
            />
          </b-col>
          <!-- #endregion::Searcher -->

          <!-- #region::Location filter -->
          <b-col
            v-if="canShowFilter(LOCATION_FIELD_NAME)"
            cols="2"
          >
            <v-select
              v-model="selectedLocation"
              label="name"
              class="style-chooser"
              placeholder="Ubicación"
              :options="locations"
            />
          </b-col>
          <!-- #endregion::Location filter -->

          <!-- #region::Order payment status filter -->
          <b-col
            v-if="showOrderPaymentStatusFilter && canShowFilter(PAYMENT_STATUS_ORDER_FIELD_NAME)"
            cols="2"
          >
            <v-select
              v-model="selectedOrderPaymentStatus"
              label="name"
              class="style-chooser"
              placeholder="Estatus pago orden"
              :options="orderPaymentStatus"
            />
          </b-col>
          <!-- #endregion::Order payment status filter -->

          <!-- #region::Payment creator -->
          <b-col
            v-if="$ability.can('read creator_order', 'Payments') && canShowFilter(PAYMENT_CREATOR_FIELD_NAME)"
            cols="2"
          >
            <vue-autosuggest
              v-model="creatorName"
              :suggestions="creatorsOptions"
              :limit="5"
              :input-props="{
                id:'autosuggest_payments_creator',
                class:'form-control',
                placeholder:'Buscar por creador'
              }"
              @input="onCreatorNameChange"
              @selected="selectedCreator = $event"
            >
              <template slot-scope="{suggestion}">
                <span class="my-suggestion-item">{{ suggestion.item.name }}</span>
              </template>
            </vue-autosuggest>
          </b-col>
          <!-- #endregion::Payment creator -->

          <!-- #region::Payment status filter -->
          <b-col
            v-if="canShowFilter(PAYMENT_STATUS_FIELD_NAME)"
            cols="2"
          >
            <v-select
              v-model="selectedPaymentStatus"
              label="name"
              class="style-chooser"
              placeholder="Estatus de pago"
              :options="paymentStatus"
            />
          </b-col>
          <!-- #endregion::Payment status filter -->

          <!-- #region::Voucher date filter -->
          <b-col
            v-if="canShowFilter(VOUCER_DATE_FIELD_NAME)"
            cols="2"
          >
            <flat-pickr
              id="voucher"
              v-model="selectedVoucherDate"
              class="form-control"
              style="background-color: white;"
              :config="config"
              placeholder="Fecha de comprobante"
              name="voucher"
              @on-close="onCloseVoucherDateFilter"
            />

            <b-button
              v-if="showClearVoucherDateFilterButton"
              variant="primary"
              class="mt-1"
              style="padding: 0.5rem 1rem;"
              @click="onClearDateFilter('voucher')"
            >
              Limpiar
            </b-button>
          </b-col>
          <!-- #endregion::Voucher date filter -->

          <!-- #region::Register date filter -->
          <b-col
            v-if="canShowFilter(REGISTER_AT_DATE_FIELD_NAME)"
            cols="2"
          >
            <flat-pickr
              v-model="selectedRegisterDate"
              class="form-control"
              style="background-color: white;"
              :config="config"
              placeholder="Fecha de registro"
              name="register"
              @on-close="onCloseVoucherDateFilter"
            />

            <b-button
              v-if="showClearRegisterDateFilterButton"
              variant="primary"
              class="mt-1"
              style="padding: 0.5rem 1rem;"
              @click="onClearDateFilter('register')"
            >
              Limpiar
            </b-button>
          </b-col>
          <!-- #endregion::Register date filter -->

          <!-- #region::Delivery cash status filter -->
          <b-col
            v-if="showCashDeliveryStatusFilter && showOnlyCashItems && canShowFilter(DELIVERY_CASH_FIELD_NAME)"
            cols="2"
          >
            <v-select
              v-model="selectedCashDeliveryStatus"
              label="name"
              class="style-chooser"
              placeholder="Entrega efectivo"
              :options="cashDeliveryStatus"
            />
          </b-col>
          <!-- #endregion::Delivery cash status filter -->

          <!-- #region::Delivery date filter -->
          <b-col
            v-if="showDeliveryDateFilter && showOnlyCashItems && canShowFilter(DELIVERY_DATE_FIELD_NAME)"
            cols="2"
          >
            <flat-pickr
              v-model="selectedDeliveryDate"
              class="form-control"
              style="background-color: white;"
              :config="config"
              placeholder="Fecha de entrega"
              name="delivery"
              @on-close="onCloseVoucherDateFilter"
            />

            <b-button
              v-if="showClearDeliveryDateFilterButton"
              variant="primary"
              class="mt-1"
              style="padding: 0.5rem 1rem;"
              @click="onClearDateFilter('delivery')"
            >
              Limpiar
            </b-button>
          </b-col>
          <!-- #endregion::Delivery date filter -->

          <!-- #region::Payment method filter -->
          <b-col
            v-if="canShowFilter(PAYMENT_METHOD_FIELD_NAME)"
            cols="2"
          >
            <v-select
              v-model="selectedPaymentMethod"
              label="method"
              class="style-chooser"
              placeholder="Método de pago"
              :options="paymentMethods"
            />
          </b-col>
          <!-- #endregion::Payment method filter -->

          <!-- #region::Payment currency filter -->
          <b-col
            v-if="canShowFilter(PAYMENT_CURRENCY_FIELD_NAME)"
            cols="2"
          >
            <v-select
              v-model="selectedPaymentCurrency"
              label="name"
              class="style-chooser"
              placeholder="Tipo de moneda"
              :options="paymentsCurrencies"
            />
          </b-col>
          <!-- #endregion::Payment currency filter -->

          <!-- #region::Order status select -->
          <b-col
            v-if="canShowFilter(ORDER_STATUS_FIELD_NAME)"
            md="2"
          >
            <v-select
              v-model="selectedOrderStatus"
              label="status"
              class="style-chooser"
              placeholder="Estatus de orden"
              :options="getOrderStatus"
            />
          </b-col>
          <!-- #endregion::Order status select -->

          <!-- #region::Order status select -->
          <b-col
            v-if="!showOnlyCashItems && canShowFilter(ORDER_STATUS_FIELD_NAME)"
            md="2"
          >
            <v-select
              v-model="selectedQuoteStatus"
              label="status"
              class="style-chooser"
              placeholder="Estatus de cotización"
              :options="getQuoteStatus"
            />
          </b-col>
          <!-- #endregion::Order status select -->

        </b-form-row>
      </b-col>
      <!-- #endregion::Columns for main filters -->

      <!-- #region::Column for table columns showing filter -->
      <b-col md="2">
        <b-form-row>

          <!-- #region::Columns filter -->
          <b-col
            cols="12"
            md="auto"
            class="ml-auto"
          >
            <b-dropdown
              variant="outline-secondary"
              class="style-chooser"
              dropright
            >

              <template slot="button-content">
                <feather-icon
                  icon="ColumnsIcon"
                  class="mr-50"
                />
              </template>

              <b-dropdown-form>

                <!-- #region::Filter columns -->
                <BasicSearcher
                  ref="filter-searcher"
                  :callback="handleSearchColumns"
                  placeholder="Buscar"
                />
                <!-- #endregion::Filter columns -->

                <b-form-checkbox
                  v-for="(column, index) in filteredColumsBySearchTerm"
                  :key="index"
                  class="mt-50"
                  :checked="column.active"
                  @change="onColumnOptionChange(column)"
                >
                  {{ column.text }}
                </b-form-checkbox>
              </b-dropdown-form>
            </b-dropdown>
          </b-col>
          <!-- #endregion::Columns filter -->

          <!-- #region::Send CSV file button -->
          <b-col
            cols="12"
            md="auto"
          >
            <b-button
              variant="flat-success"
              class="csv-btn float-right"
              :disabled="isSendingEmail"
              @click="onSendingEmail"
            >
              <b-spinner
                v-if="isSendingEmail"
                small
              />
              <feather-icon
                v-if="!isSendingEmail"
                icon="MailIcon"
              />
            </b-button>
          </b-col>
          <!-- #endregion::Send CSV file button -->

        </b-form-row>
      </b-col>
      <!-- #endregion::Column for table columns showing filter -->

    </b-form-row>

  </div>
</template>

<script>
// #region Imports
import { mapGetters, mapActions } from 'vuex'
import {
  BCol, BFormRow, BDropdown, BDropdownForm, BFormCheckbox, BButton, BSpinner,
} from 'bootstrap-vue'

import { VueAutosuggest } from 'vue-autosuggest'
import vSelect from 'vue-select'
import BasicSearcher from '@/components/forms/BasicSearcher.vue'

import flatPickr from 'vue-flatpickr-component'
// eslint-disable-next-line no-unused-vars, import/no-extraneous-dependencies, import/extensions
import { Spanish } from 'flatpickr/dist/l10n/es.js'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'flatpickr/dist/themes/material_blue.css'
// eslint-disable-next-line import/no-extraneous-dependencies
import confirmDatePlugin from 'flatpickr/dist/plugins/confirmDate/confirmDate'

import getError from '@/helpers/ErrorsHandler'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
// #endregion

const LOCATION_FIELD_NAME = 'Location'
const PAYMENT_CREATOR_FIELD_NAME = 'Creator'
const VOUCER_DATE_FIELD_NAME = 'VoucherDate'
const ORDER_STATUS_FIELD_NAME = 'OrderStatus'
const PAYMENT_CURRENCY_FIELD_NAME = 'Currency'
const DELIVERY_CASH_FIELD_NAME = 'DeliveryCash'
const DELIVERY_DATE_FIELD_NAME = 'DeliveryDate'
const PAYMENT_METHOD_FIELD_NAME = 'PaymentMethod'
const REGISTER_AT_DATE_FIELD_NAME = 'RegisteredAt'
const PAYMENT_STATUS_ORDER_FIELD_NAME = 'PaymentStatus'
const PAYMENT_STATUS_FIELD_NAME = 'PaymentConfirmation'

export default {
  components: {
    BCol,
    vSelect,
    BButton,
    BSpinner,
    BFormRow,
    flatPickr,
    BDropdown,
    BDropdownForm,
    BFormCheckbox,
    BasicSearcher,
    VueAutosuggest,
  },
  props: {
    /**
     * Titulo que se muestra en la pestaña
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * Indica si se muestra el filtro de pagos de orden
     */
    showOrderPaymentStatusFilter: {
      type: Boolean,
      default: true,
    },
    /**
     * Indica si se muestra el filtro de entrega de efectivo
     */
    showCashDeliveryStatusFilter: {
      type: Boolean,
      default: true,
    },
    /**
     * Indica si se muestra el filtro de fecha de entrega
     */
    showDeliveryDateFilter: {
      type: Boolean,
      default: true,
    },
    /**
     * Enlace base para descargar el archivo CSV
     */
    downloadFileBaseLink: {
      type: String,
      required: true,
    },
    /**
     * Indica si se muestran solo pagos en efectivo
     */
    showOnlyCashItems: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      /**
       * For creator filter
       */
      creatorsOptions: [],
      selectedCreator: '',

      selectedLocation: null,
      selectedOrderPaymentStatus: null,
      selectedPaymentStatus: null,
      selectedCashDeliveryStatus: null,
      selectedPaymentMethod: null,
      selectedPaymentCurrency: null,
      selectedOrderStatus: null,
      selectedQuoteStatus: null,

      /**
       * Dates ranges
       */
      selectedVoucherDate: null,
      selectedRegisterDate: null,
      selectedDeliveryDate: null,

      showClearVoucherDateFilterButton: false,
      showClearRegisterDateFilterButton: false,
      showClearDeliveryDateFilterButton: false,

      isSendingEmail: false,
      columnsSearchTerm: '',

      /**
       * Config params for date range picker
       */
      config: {
        mode: 'range',
        locale: Spanish,
        ignoredFocusElements: [window.document.body],
        plugins: [confirmDatePlugin({
          confirmIcon: "<span style='cursor: pointer;'>Aceptar <i class='fa fa-check my-1'></i></span>",
          confirmText: '',
          showAlways: false,
          theme: 'light', // or "dark"
        })],
      },

      LOCATION_FIELD_NAME,
      VOUCER_DATE_FIELD_NAME,
      ORDER_STATUS_FIELD_NAME,
      DELIVERY_CASH_FIELD_NAME,
      DELIVERY_DATE_FIELD_NAME,
      PAYMENT_METHOD_FIELD_NAME,
      PAYMENT_STATUS_FIELD_NAME,
      PAYMENT_CREATOR_FIELD_NAME,
      PAYMENT_CURRENCY_FIELD_NAME,
      REGISTER_AT_DATE_FIELD_NAME,
      PAYMENT_STATUS_ORDER_FIELD_NAME,
    }
  },
  computed: {
    ...mapGetters({
      getLocations: 'payments/getLocations',
      getOrderStatus: 'payments/getOrderStatus',
      getQuoteStatus: 'payments/getQuoteStatus',
      getPaymentStatus: 'payments/getPaymentStatus',
      getCreatorsSuggests: 'payments/getCreatorsSuggests',
      getOrderPaymentStatus: 'payments/getOrderPaymentStatus',
      getCashDeliveryStatus: 'payments/getCashDeliveryStatus',
      getPaymentsCurrencies: 'payments/getPaymentsCurrencies',
      /**
       * Métodos de pagos
       */
      getQuotesPaymentMethods: 'quotes/getPaymentMethods',
      getRefundsPaymentMethods: 'refunds/getPaymentMethods',
      /**
       * Filtros globales
       */
      getListsParams: 'payments/getListsParams',
      getFilteredCreator: 'filters/getFilteredCreator',
      getFilteredLocation: 'filters/getFilteredLocation',
      getFilteredSearchTerm: 'filters/getFilteredSearchTerm',
      getFilteredOrderStatus: 'filters/getFilteredOrderStatus',
      getFilteredQuoteStatus: 'filters/getFilteredQuoteStatus',
      getFilteredPaymentStatus: 'filters/getFilteredPaymentStatus',
      getFilteredPaymentMethod: 'filters/getFilteredPaymentMethod',
      getFilteredPaymentCurrency: 'filters/getFilteredPaymentCurrency',
      getFilteredVoucherDateRange: 'filters/getFilteredVoucherDateRange',
      getFilteredRegisterDateRange: 'filters/getFilteredRegisterDateRange',
      getFilteredDeliveryDateRange: 'filters/getFilteredDeliveryDateRange',
      getFilteredOrderPaymentStatus: 'filters/getFilteredOrderPaymentStatus',
      getFilteredCashDeliveryStatus: 'filters/getFilteredCashDeliveryStatus',
    }),
    /**
     * Ubicaciones para filtrar
     */
    locations: {
      get() { return this.getLocations },
    },
    /**
     * Estatus de pago para filtrar
     */
    paymentStatus: {
      get() { return this.getPaymentStatus },
    },
    /**
     * Estatus de pago de orden para filtrar
     */
    orderPaymentStatus: {
      get() { return this.getOrderPaymentStatus },
    },
    /**
     * Estatus de entrega de efectivo para filtrar
     */
    cashDeliveryStatus: {
      get() { return this.getCashDeliveryStatus },
    },
    /**
     * Tipos de moneda para filtrar
     */
    paymentsCurrencies: {
      get() { return this.getPaymentsCurrencies },
    },
    /**
     * Métodos de pago para filtrar
     */
    paymentMethods: {
      get() {
        switch (this.title) {
          case 'refunds':
            return this.getRefundsPaymentMethods
          case 'cash':
            return this.getQuotesPaymentMethods.filter(
              p => p.method === 'Efectivo M/N' || p.method === 'Dólares',
            )
          default:
            return this.getQuotesPaymentMethods
        }
      },
    },
    /**
     * Filtro de creador (filtro en store)
     */
    filteredCreator: {
      get() { return this.getFilteredCreator },
      set(value) { this.setFilteredCreator(value) },
    },
    /**
     * Indica la ubicación seleccionada (filtro global)
     */
    filteredLocation: {
      get() { return this.getFilteredLocation },
      set(value) { this.setFilteredLocation(value) },
    },
    /**
     * Indica el término de búsqueda seleccionado (filtro global)
     */
    filteredSearchTerm: {
      get() { return this.getFilteredSearchTerm },
    },
    /**
     * Indica el estatus de orden seleccionado (filtro global)
     */
    filteredOrderStatus: {
      get() { return this.getFilteredOrderStatus },
      set(value) { this.setFilteredOrderStatus(value) },
    },
    /**
     * Indica el estatus de cotización seleccionado (filtro global)
     */
    filteredQuoteStatus: {
      get() { return this.getFilteredQuoteStatus },
      set(value) { this.setFilteredQuoteStatus(value) },
    },
    /**
     * Indica el estatus de pago seleccionado (filtro global)
     */
    filteredPaymentStatus: {
      get() { return this.getFilteredPaymentStatus },
      set(value) { this.setFilteredPaymentStatus(value) },
    },
    /**
     * Indica el método de pago seleccionado (filtro global)
     */
    filteredPaymentMethod: {
      get() { return this.getFilteredPaymentMethod },
      set(value) { this.setFilteredPaymentMethod(value) },
    },
    /**
     * Indica el tipo de moneda seleccionado (filtro global)
     */
    filteredPaymentCurrency: {
      get() { return this.getFilteredPaymentCurrency },
      set(value) { this.setFilteredPaymentCurrency(value) },
    },
    /**
     * Indica el rango de fechas de comprobante seleccionado (filtro global)
     */
    filteredVoucherDateRange: {
      get() { return this.getFilteredVoucherDateRange },
      set(value) { this.setFilteredVoucherDateRange(value) },
    },
    /**
     * Indica el rango de fechas de registro seleccionado (filtro global)
     */
    filteredRegisterDateRange: {
      get() { return this.getFilteredRegisterDateRange },
      set(value) { this.setFilteredRegisterDateRange(value) },
    },
    /**
     * Indica el rango de fechas de entrega seleccionado (filtro global)
     */
    filteredDeliveryDateRange: {
      get() { return this.getFilteredDeliveryDateRange },
      set(value) { this.setFilteredDeliveryDateRange(value) },
    },
    /**
     * Indica el estatus de pago de orden seleccionado (filtro global)
     */
    filteredOrderPaymentStatus: {
      get() { return this.getFilteredOrderPaymentStatus },
      set(value) { this.setFilteredOrderPaymentStatus(value) },
    },
    /**
     * Indica el estatus de entrega de efectivo seleccionado (filtro global)
     */
    filteredCashDeliveryStatus: {
      get() { return this.getFilteredCashDeliveryStatus },
      set(value) { this.setFilteredCashDeliveryStatus(value) },
    },
    /**
     * Columnas de la pestala seleccionada, valida si se deben mostrar las columna de proveedor
     */
    filteredColumns: {
      get() {
        const { selectedColumns } = this.getListsParams.find(p => p.title === this.title)
        return selectedColumns
      },
    },
    /**
     * Columnas filtradas por término de búsqueda del filtro de columnas
     */
    filteredColumsBySearchTerm: {
      get() {
        return this.filteredColumns.filter(c => c.text.toLowerCase().includes(this.columnsSearchTerm.toLowerCase()))
      },
    },
    creatorName: {
      get() { return this.filteredCreator?.name || '' },
      set() {},
    },
  },

  /**
  * Carga información del estado
  *
  * @summary Si existe información relacionada con los filtros cargados en el store,
  * se cargan en el componente
  */
  mounted() {
    // Carga el término de búsqueda (filtro en store)
    if (this.filteredSearchTerm) {
      this.$refs['basic-searcher'].loadSearchTerm(this.filteredSearchTerm)
    }

    // Carga el estatus de orden (filtro en store)
    this.selectedOrderStatus = this.filteredOrderStatus || null
    this.$watch('selectedOrderStatus', status => {
      this.$emit('on:filter', { type: 'orderStatus', value: status !== null ? status : '' })
    })

    // Carga el estatus de cotización (filtro en store)
    this.selectedQuoteStatus = this.filteredQuoteStatus || null
    this.$watch('selectedQuoteStatus', status => {
      this.$emit('on:filter', { type: 'quoteStatus', value: status !== null ? status : '' })
    })

    // Carga el creador (filtro en store)
    this.selectedCreator = this.filteredCreator ? { item: this.filteredCreator } : ''
    this.$watch('selectedCreator', creator => {
      this.$emit('on:filter', { type: 'creator', value: creator !== null ? creator.item : '' })
    })

    // Carga la ubicación (filtro en store)
    this.selectedLocation = this.filteredLocation || null
    this.$watch('selectedLocation', location => {
      this.$emit('on:filter', { type: 'location', value: location !== null ? location.name : '' })
    })

    // Carga el estatus de pago (filtro en store)
    this.selectedPaymentStatus = this.filteredPaymentStatus || null
    this.$watch('selectedPaymentStatus', status => {
      this.$emit('on:filter', { type: 'paymentStatus', value: status !== null ? status.name : '' })
    })

    // Carga el método de pago (filtro en store)
    this.selectedPaymentMethod = this.filteredPaymentMethod || null
    this.$watch('selectedPaymentMethod', method => {
      this.$emit('on:filter', { type: 'paymentMethod', value: method !== null ? method.method : '' })
    })

    // Carga el tipo de moneda (filtro en store)
    this.selectedPaymentCurrency = this.filteredPaymentCurrency || null
    this.$watch('selectedPaymentCurrency', currency => {
      this.$emit('on:filter', { type: 'paymentCurrency', value: currency !== null ? currency.name : '' })
    })

    // Carga el estatus de pago de orden (filtro en store)
    this.selectedOrderPaymentStatus = this.filteredOrderPaymentStatus || null
    this.$watch('selectedOrderPaymentStatus', status => {
      this.$emit('on:filter', { type: 'orderPaymentStatus', value: status !== null ? status.name : '' })
    })

    // Carga el estatus de entrega de efectivo (filtro en store)
    this.selectedCashDeliveryStatus = this.filteredCashDeliveryStatus || null
    this.$watch('selectedCashDeliveryStatus', status => {
      this.$emit('on:filter', { type: 'cashDeliveryStatus', value: status !== null ? status.name : '' })
    })

    // Carga el estado de rango de fechas de comprobante (filtro en store)
    if (this.filteredVoucherDateRange) {
      this.selectedVoucherDate = `${this.filteredVoucherDateRange.from} a ${this.filteredVoucherDateRange.to}`
      this.showClearVoucherDateFilterButton = true
    } else {
      this.selectedVoucherDate = null
      this.showClearVoucherDateFilterButton = false
    }

    // Carga el estado de rango de fechas de registro (filtro en store)
    if (this.filteredRegisterDateRange) {
      this.selectedRegisterDate = `${this.filteredRegisterDateRange.from} a ${this.filteredRegisterDateRange.to}`
      this.showClearRegisterDateFilterButton = true
    } else {
      this.selectedRegisterDate = null
      this.showClearRegisterDateFilterButton = false
    }

    // Carga el estado de rango de fechas de entrega (filtro en store)
    if (this.filteredDeliveryDateRange) {
      this.selectedDeliveryDate = `${this.filteredDeliveryDateRange.from} a ${this.filteredDeliveryDateRange.to}`
      this.showClearDeliveryDateFilterButton = true
    } else {
      this.selectedDeliveryDate = null
      this.showClearDeliveryDateFilterButton = false
    }
  },

  methods: {
    ...mapActions({
      sendPaymentsCSVFile: 'payments/sendPaymentsCSVFile',
      /**
       * Acciones para filtros globales
       */
      setFilteredCreator: 'filters/setFilteredCreator',
      setFilteredLocation: 'filters/setFilteredLocation',
      setFilteredOrderStatus: 'filters/setFilteredOrderStatus',
      setFilteredQuoteStatus: 'filters/setFilteredQuoteStatus',
      setFilteredPaymentStatus: 'filters/setFilteredPaymentStatus',
      setFilteredPaymentMethod: 'filters/setFilteredPaymentMethod',
      setFilteredPaymentCurrency: 'filters/setFilteredPaymentCurrency',
      setFilteredVoucherDateRange: 'filters/setFilteredVoucherDateRange',
      setFilteredRegisterDateRange: 'filters/setFilteredRegisterDateRange',
      setFilteredDeliveryDateRange: 'filters/setFilteredDeliveryDateRange',
      setFilteredOrderPaymentStatus: 'filters/setFilteredOrderPaymentStatus',
      setFilteredCashDeliveryStatus: 'filters/setFilteredCashDeliveryStatus',
    }),

    /**
    * Evento del buscador básico
    *
    * @summary Emite un evento con el término de búsqueda y limpia los campos de selección
    * @param {String} searchTerm - Término de búsqueda
    */
    handleSearch(searchTerm) {
      this.selectedLocation = null
      this.filteredLocation = null

      this.selectedOrderStatus = null
      this.filteredOrderStatus = null

      this.selectedQuoteStatus = null
      this.filteredQuoteStatus = null

      this.selectedOrderPaymentStatus = null
      this.filteredOrderPaymentStatus = null

      this.selectedPaymentStatus = null
      this.filteredPaymentStatus = null

      this.selectedPaymentMethod = null
      this.filteredPaymentMethod = null

      this.selectedPaymentCurrency = null
      this.filteredPaymentCurrency = null

      this.selectedCashDeliveryStatus = null
      this.filteredCashDeliveryStatus = null

      this.onClearDateFilter('voucher', false)
      this.filteredVoucherDateRange = null

      this.onClearDateFilter('register', false)
      this.filteredRegisterDateRange = null

      this.onClearDateFilter('delivery', false)
      this.filteredDeliveryDateRange = null

      this.selectedCreator = ''
      this.filteredCreator = ''
      this.creatorsOptions = []

      this.$emit('on:filter', { type: 'search', value: searchTerm })
    },

    /**
    * Evento del buscador de filtro de columnas
    *
    * @summary Atiende el evento de búsqueda del filtro de columnas
    * @param {String} searchTerm - Término de búsqueda
    */
    handleSearchColumns(searchTerm) {
      this.columnsSearchTerm = searchTerm
    },

    /**
    * Evento de cambio de nombre de creador
    *
    * @summary Emite un evento cuando se ingresan caracteres en el buscador de creador
    * @param {String} text - Término de búsqueda
    */
    onCreatorNameChange(text) {
      if (text === '' || text === undefined) {
        this.creatorsOptions = []
        this.$emit('on:filter', { type: 'creator', value: '' })
        return
      }

      const filteredData = this.getCreatorsSuggests
        .filter(item => `${item.Name} ${item?.LastName}`.toLowerCase().indexOf(text.toLowerCase()) > -1)
        .slice(0, this.limit)

      const namedSuggests = []

      filteredData.forEach(item => {
        namedSuggests.push({ name: `${item.Name} ${item?.LastName || ''}`, id: item.IdUser })
      })
      this.creatorsOptions = [{
        data: namedSuggests,
      }]
    },

    /**
    * Selección de columnas
    * @summary Emite un evento con la columna seleccionada y su estado (activa o inactiva)
    * @param {Object} column - Columna seleccionada
    */
    onColumnOptionChange(column) {
      const baseColumn = column
      baseColumn.active = !baseColumn.active
      this.$emit('on:column-option-change', baseColumn)

      if (baseColumn.active) {
        return
      }

      if (baseColumn.value === LOCATION_FIELD_NAME && this.selectedLocation) {
        this.selectedLocation = null
      }

      if (baseColumn.value === ORDER_STATUS_FIELD_NAME && (this.selectedOrderStatus || this.selectedQuoteStatus)) {
        this.selectedOrderStatus = null
        this.selectedQuoteStatus = null
      }

      if (baseColumn.value === PAYMENT_CREATOR_FIELD_NAME && this.selectedCreator) {
        this.selectedCreator = ''
        this.creatorsOptions = []
      }

      if (baseColumn.value === PAYMENT_STATUS_ORDER_FIELD_NAME && this.selectedOrderPaymentStatus) {
        this.selectedOrderPaymentStatus = null
      }

      if (baseColumn.value === PAYMENT_STATUS_FIELD_NAME && this.selectedPaymentStatus) {
        this.selectedPaymentStatus = null
      }

      if (baseColumn.value === PAYMENT_METHOD_FIELD_NAME && this.selectedPaymentMethod) {
        this.selectedPaymentMethod = null
      }

      if (baseColumn.value === PAYMENT_CURRENCY_FIELD_NAME && this.selectedPaymentCurrency) {
        this.selectedPaymentCurrency = null
      }

      if (baseColumn.value === VOUCER_DATE_FIELD_NAME && this.selectedVoucherDate) {
        this.onClearDateFilter('voucher')
      }

      if (baseColumn.value === REGISTER_AT_DATE_FIELD_NAME && this.selectedRegisterDate) {
        this.onClearDateFilter('register')
      }

      if (baseColumn.value === DELIVERY_DATE_FIELD_NAME && this.selectedDeliveryDate) {
        this.onClearDateFilter('delivery')
      }

      if (baseColumn.value === DELIVERY_CASH_FIELD_NAME && this.selectedCashDeliveryStatus) {
        this.selectedCashDeliveryStatus = null
      }
    },

    /**
     * Descarga el archivo PDF
     *
     */
    async onSendingEmail() {
      try {
        this.isSendingEmail = true
        await this.sendPaymentsCSVFile({
          url: this.downloadFileBaseLink,
          loadParams: {
            cashTerm: this.showOnlyCashItems ? 1 : 0,
            locationPaymentTerm: this.filteredLocation,
            orderStatusTerm: this.filteredOrderStatus,
            quoteStatusTerm: this.filteredQuoteStatus,
            searchTerm: this.filteredSearchTerm,
            paymentStatusTerm: this.filteredOrderPaymentStatus,
            creatorTerm: this.filteredCreator,
            statusConfirmationTerm: this.filteredPaymentStatus,
            paymentMethodTerm: this.filteredPaymentMethod,
            paymentCurrencyTerm: this.filteredPaymentCurrency,
            deliveryCashTerm: this.filteredCashDeliveryStatus,
            voucherDateStartTerm: this.filteredVoucherDateRange?.from,
            voucherDateEndTerm: this.filteredVoucherDateRange?.to,
            registerDateStartTerm: this.filteredRegisterDateRange?.from,
            registerDateEndTerm: this.filteredRegisterDateRange?.to,
            deliveryDateStartTerm: this.filteredDeliveryDateRange?.from,
            deliveryDateEndTerm: this.filteredDeliveryDateRange?.to,
            /**
             * Columns list
             */
            activedColumnsTerm: this.filteredColumns.filter(c => c.active),
          },
        })
        this.showToast(
          'Correo enviado',
          'El correo electrónico ha sido enviado correctamente.',
          'success',
        )
      } catch (error) {
        this.showToast('Error de validación', getError(error), 'danger')
      } finally {
        this.isSendingEmail = false
      }
    },

    /**
    * Evento de selección de fecha
    *
    * @summary Atiendo al evento de selección de fecha del componente flatpickr
    * @param {String} date - Cadena de texto con las fechas seleccionadas
    */
    onCloseVoucherDateFilter(_, date, instance) {
      const from = date?.split('a')[0]?.trim()
      const to = date?.split('a')[1]?.trim()

      let dateRange = null

      if (from && to) {
        dateRange = { from, to }
      } else if (from && !to) {
        dateRange = { from, to: from }
      }

      if (dateRange) {
        // console.log(instance);
        switch (instance.element.name) {
          case 'voucher':
            // this.selectedVoucherDate = dateRange
            this.showClearVoucherDateFilterButton = true
            this.$emit('on:filter', { type: 'voucherDate', value: dateRange !== null ? dateRange : '' })
            break
          case 'register':
            // this.selectedRegisterDate = dateRange
            this.showClearRegisterDateFilterButton = true
            this.$emit('on:filter', { type: 'registerDate', value: dateRange !== null ? dateRange : '' })
            break
          default:
            // this.selectedDeliveryDate = dateRange
            this.showClearDeliveryDateFilterButton = true
            this.$emit('on:filter', { type: 'deliveryDate', value: dateRange !== null ? dateRange : '' })
            break
        }
      }
    },

    /**
    * Limpieza del campo de rangos de fecha
    *
    * @summary Atiende al evento de limpieza del campo de rangos de fecha
    */
    onClearDateFilter(type, emitEvent = true) {
      switch (type) {
        case 'voucher':
          this.selectedVoucherDate = null
          this.showClearVoucherDateFilterButton = false
          if (emitEvent) {
            this.$emit('on:filter', { type: 'voucherDate', value: '' })
          }
          break
        case 'register':
          this.selectedRegisterDate = null
          this.showClearRegisterDateFilterButton = false
          if (emitEvent) {
            this.$emit('on:filter', { type: 'registerDate', value: '' })
          }
          break
        default:
          this.selectedDeliveryDate = null
          this.showClearDeliveryDateFilterButton = false
          if (emitEvent) {
            this.$emit('on:filter', { type: 'deliveryDate', value: '' })
          }
          break
      }
    },

    /**
     * Indica si se debe mostrar el filtro de columnas
     *
     * @param {String} value - Valor de la columna
     */
    canShowFilter(value) {
      const checkedColumns = this.filteredColumns.filter(c => c.active && c.value === value)
      return checkedColumns.length > 0
    },

    /**
    * Muestra un mensaje de tipo toast
    *
    * @param {string} title - Título del mensaje
    * @param {string} text - Contenido del mensaje
    * @param {string} variant - Tipo del mensaje (warning, success, danger)
    */
    showToast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, variant, icon: 'BellIcon',
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/vue-autosuggest.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
[class*="collapse-"] {
  .card {
    .card-header {
      cursor: auto;
      padding: 1rem 2.8rem 1rem 1rem;
    }
  }
}
.style-chooser {
  background: #ffffff;
}

.col-padding {
  padding: 0 0.5rem 0 0.5rem;
}

.btn-outline-secondary {
  border: 1px solid !important;
  border-color: #c9c9c9 !important;
}

.dropdown-menu {
  min-width: 16rem !important;
}

.vs__selected button {
  display: none
}
</style>
